/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { get, identity, omit, startCase } from 'lodash';
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import { PenaltyCreateEdit } from './create-edit';
import { TableList } from 'components/table-list';
import PenaltyService from 'services/penalty';

import { ProfileContext } from 'contexts/profile';

import { convertNumberToRupiahCurrency, errHandling } from 'utils';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;
const { confirm } = Modal;

export function PenaltyDetail() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);
  const navigate = useNavigate();
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadStatus, setLoadStatus] = useState(true);
  const [modalContent, setModalContent] = useState({});
  const [penaltyList, setPenaltyList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const parsed = queryString.parse(location.search);

  const { id: productId, productName } = parsed;

  const buttonAction = (row = {}) => {
    const { id = '', title = '', tempId = '' } = row;
    return (
      <Row className="button-table">
        <Col lg={12} className="button">
          <PdButton
            className="height-32"
            full
            onClick={handleEdit.bind(this, row)}
            type="primary">
            Edit
          </PdButton>
        </Col>
        <Col lg={12} className="button">
          <PdButton
            className="height-32"
            disabled={title.toLowerCase() === 'other'}
            full
            onClick={handleDelete.bind(this, title, id, tempId)}
            type="danger">
            Delete
          </PdButton>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      name: 'Inspection List',
      selector: row => row.title,
      width: '25%',
      center: true,
    },
    {
      name: 'Description',
      selector: row => row.description,
      width: '30%',
      center: true,
    },
    {
      name: 'Penalty Price',
      selector: row => row.price,
      format: (row) => convertNumberToRupiahCurrency(+row.price),
      width: '20%',
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function handleEdit(penaltyData) {
    setModalContent(penaltyData);
    setShowModal(true);
  }

  function handleDelete(listName, penaltyId, tempIdParam) {
    confirm({
      title: `Are you sure you want to delete ‘${listName}’ from the inspection list?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        if (penaltyId) handleConfirmedDeletePenalty(penaltyId);
        else
          setPenaltyList((prev) =>
            prev.filter(({ tempId } = {}) => tempId !== tempIdParam)
          );
      },
    });
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    handleCloseModal();
  }

  async function handleConfirmedDeletePenalty(penaltyId) {
    setLoadStatus(true);
    try {
      await PenaltyService.deletePenalty(penaltyId);
      fetchPenaltyDetail();
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function fetchPenaltyDetail() {
    setLoadStatus(true);
    try {
      const {
        data: { data },
      } = await PenaltyService.getListPenalty(productId);

      const hasOtherDamage = data.some(
        ({ title } = '') => title.toLowerCase() === 'other'
      );

      // continue if other damage already in the list
      if (hasOtherDamage) {
        setPenaltyList(data);
        return;
      }

      // need to create other damage on every rentable product
      await PenaltyService.createPenalty({
        damage: {
          description: 'Other Damage',
          price: 0,
          product_id: productId,
          title: 'Other',
        },
      });
      const {
        data: { data: penaltyListWithOtherDamage },
      } = await PenaltyService.getListPenalty(productId);
      setPenaltyList(penaltyListWithOtherDamage);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function handleSubmit() {
    setLoadingButton(true);
    try {
      const payload = penaltyList.map((singlePenalty) => {
        // if has id then it's edit
        const { title, description, price, id } = singlePenalty;
        if (id) {
          return PenaltyService.updatePenalty(singlePenalty.id, {
            damage: singlePenalty,
          });
        } else {
          return PenaltyService.createPenalty({
            damage: {
              description,
              price,
              product_id: productId,
              title,
            },
          });
        }
      });
      await Promise.all(payload);
      message.success('Successfully add penalty list');
      navigate('/penalties');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton(false);
    }
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productId) fetchPenaltyDetail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  return (
    <Layout className="main-container pd-cms-penalty-detail">
      <div className="content">
        <div className="pageTitle">{productName || 'Penalty Detail'}</div>
        <Content className="pageContent">
          <Row
            gutter={[18, 36]}
            className="pd-margin-bottom-md"
            type="flex"
            align="middle">
            <Col lg={{ span: 4, offset: 19 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={setShowModal.bind(this, true)}>
                Add New List
              </PdButton>
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                columns={columns}
                loadingOrder={loadStatus}
                orderList={penaltyList}
                responsive={false}
              />
            )}
          </Row>

          <Row className="pd-margin-top-xl" type="flex" gutter={8}>
            <Col lg={8}>
              <PdButton
                className="height-32"
                type="danger"
                full
                onClick={() => navigate(-1)}>
                Back
              </PdButton>
            </Col>
            <Col lg={8}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={handleSubmit}>
                Publish
              </PdButton>
            </Col>
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <PenaltyCreateEdit
            closeModal={handleCloseCreateEdit}
            penalty={modalContent}
            warehouseList={[]}
            product_id={productId}
            setPenaltyList={setPenaltyList}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default PenaltyDetail;
